import { useMemo, useRef, useState, type CSSProperties, type DragEvent } from "react";
import { Link } from "react-router-dom";
import { data } from "../lib/data";
import {
  experiencesByTag,
  getTechnology,
  projectsByTag,
  technologyUsage,
} from "../lib/queries";
import {
  TECH_CAT_HUE,
  TECH_GROUPS,
  lensHueFor,
  lensIconFor,
  techCatIcon,
  techGroupIcon,
} from "../lib/visuals";
import type { Tag } from "../types";

export type GridLens = "industry" | "skill" | "role";

const LENS_PARAM: Record<GridLens, string> = {
  industry: "industry",
  skill: "skill",
  role: "role",
};

/* Count noun per lens (count = # of roles/engagements featuring the item). */
const LENS_COUNT_NOUN: Record<GridLens, string> = {
  industry: "engagements",
  skill: "roles",
  role: "positions",
};

/* Author-specified display order for the Skill grid (read down each column of
   the 3-col layout → so the row-major sequence interleaves the three columns). */
const SKILL_GRID_ORDER = [
  "program-management", "customer-engagement", "cloud-transformation",
  "team-building", "amazon-connect-sme", "architecture",
  "proposals-pricing", "marketing-automation", "tech-enablement",
  "vendor-management", "client-onboarding", "ai-driven-delivery",
];

function usedIndustryIds(): Set<string> {
  const s = new Set<string>();
  for (const e of data.experiences) for (const id of e.industries) s.add(id);
  for (const p of data.projects) for (const id of p.industries) s.add(id);
  return s;
}

function gridOptions(lens: GridLens): Tag[] {
  if (lens === "industry") {
    const used = usedIndustryIds();
    return data.industries.filter((i) => used.has(i.id));
  }
  if (lens === "skill") {
    const rank = (id: string) => {
      const i = SKILL_GRID_ORDER.indexOf(id);
      return i === -1 ? 999 : i;
    };
    return [...data.skills].sort((a, b) => rank(a.id) - rank(b.id));
  }
  return data.roles;
}

function countFor(lens: GridLens, id: string): number {
  const exps = experiencesByTag(lens, id).length;
  if (lens === "role") return exps;
  return exps + projectsByTag(lens, id).length;
}

/* Rich 3-column directory with a color line sized by frequency — used for the
   Industry / Skill / Role lens carousels. Skill & Role cards are drag-n-drop
   re-orderable; the order persists locally and "Reset order" returns to the
   authored default. */
const LENS_ORDER_KEY = (lens: GridLens) => `atlas-lens-order-${lens}`;

export function LensGrid({ lens }: { lens: GridLens }) {
  const opts = useMemo(() => gridOptions(lens), [lens]);
  const reorderable = lens === "skill" || lens === "role";
  const defaultIds = opts.map((o) => o.id);
  const [saved, setSaved] = useState<string[] | null>(() => {
    if (!reorderable) return null;
    try {
      const s = JSON.parse(localStorage.getItem(LENS_ORDER_KEY(lens)) ?? "null");
      return Array.isArray(s) ? s : null;
    } catch {
      return null;
    }
  });
  const [liveIds, setLiveIds] = useState<string[] | null>(null); // during a drag
  const dragFrom = useRef<number | null>(null);

  const ids =
    liveIds ??
    (saved
      ? [
          ...saved.filter((id) => defaultIds.includes(id)),
          ...defaultIds.filter((id) => !saved.includes(id)),
        ]
      : defaultIds);
  const byId = new Map(opts.map((o) => [o.id, o]));
  const ordered = ids.map((id) => byId.get(id)).filter((o): o is Tag => Boolean(o));

  const onDragStart = (i: number) => (e: DragEvent<HTMLLIElement>) => {
    if (!reorderable) return;
    dragFrom.current = i;
    setLiveIds(ids);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", ids[i]);
    } catch {
      // some engines disallow setData on synthetic drags
    }
  };
  const onDragOver = (i: number) => (e: DragEvent<HTMLLIElement>) => {
    if (!reorderable || dragFrom.current == null) return;
    e.preventDefault();
    if (i === dragFrom.current) return;
    setLiveIds((cur) => {
      const arr = [...(cur ?? ids)];
      const [moved] = arr.splice(dragFrom.current as number, 1);
      arr.splice(i, 0, moved);
      dragFrom.current = i;
      return arr;
    });
  };
  const onDragEnd = () => {
    if (!reorderable || dragFrom.current == null) return;
    dragFrom.current = null;
    setLiveIds((cur) => {
      if (cur) {
        setSaved(cur);
        localStorage.setItem(LENS_ORDER_KEY(lens), JSON.stringify(cur));
      }
      return null;
    });
  };
  const resetOrder = () => {
    localStorage.removeItem(LENS_ORDER_KEY(lens));
    setSaved(null);
    setLiveIds(null);
  };

  const counts = ordered.map((o) => ({ o, c: countFor(lens, o.id) }));
  const maxC = Math.max(1, ...counts.map((x) => x.c));
  const noun = LENS_COUNT_NOUN[lens] ?? "items";
  return (
    <div>
      {reorderable && saved && (
        <div className="lens-grid-tools">
          <button type="button" className="careerroad-reset mono" onClick={resetOrder}>
            Reset order
          </button>
        </div>
      )}
      <ul className="ind-grid">
        {counts.map(({ o, c }, i) => {
          const hue = lensHueFor(lens, o.id);
          const ItemIcon = lensIconFor(lens, o.id);
          return (
            <li
              key={o.id}
              draggable={reorderable || undefined}
              className={reorderable ? "lens-reorder-item" : undefined}
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver(i)}
              onDragEnd={onDragEnd}
            >
              <Link
                to={`/by/${lens}?${LENS_PARAM[lens]}=${o.id}`}
                className="ind-card card card-link"
              >
                <span
                  className="ind-ico-soft"
                  style={{
                    color: hue,
                    background: `color-mix(in oklab, ${hue} 14%, var(--card))`,
                    borderColor: `color-mix(in oklab, ${hue} 30%, var(--rule))`,
                  }}
                >
                  <ItemIcon size={20} />
                </span>
                <div className="grow">
                  <div className="ind-label">{o.label}</div>
                  <div className="ind-count mono">
                    {c} {c === 1 ? noun.slice(0, -1) : noun}
                  </div>
                </div>
                <div className="ind-bar-soft">
                  <span
                    style={{
                      width: `${Math.max(10, (c / maxC) * 100)}%`,
                      background: `color-mix(in oklab, ${hue} 60%, white)`,
                    }}
                  />
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Technology — grouped by domain, sorted by recency ─────────────────────

const FAVICON = (domain: string, size = 128) =>
  `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${domain}&size=${size}`;

/* Brand favicons for individual technologies (everything else → category icon) */
const TECH_DOMAIN: Record<string, string> = {
  "amazon-connect": "aws.amazon.com",
  "amazon-q-connect": "aws.amazon.com",
  "aws-bedrock": "aws.amazon.com",
  "amazon-lex": "aws.amazon.com",
  "bedrock-agentcore": "aws.amazon.com",
  "claude-code": "claude.ai",
  "claude-design": "claude.ai",
  "claude-cowork": "claude.ai",
  chatgpt: "openai.com",
  gemini: "google.com",
  salesforce: "salesforce.com",
  "salesforce-marketing-cloud": "salesforce.com",
  servicenow: "servicenow.com",
  zoho: "zoho.com",
  zendesk: "zendesk.com",
  "amdocs-connectx": "amdocs.com",
  websphere: "ibm.com",
  "websphere-portal": "ibm.com",
  "ibm-maximo": "ibm.com",
  "aix-vio": "ibm.com",
  "lotus-ei": "ibm.com",
  "lotus-notes": "ibm.com",
  "sap-hana": "sap.com",
  "sap-rfc": "sap.com",
  "oracle-db": "oracle.com",
  java: "java.com",
  "adobe-pdf-sdk": "adobe.com",
  checkr: "checkr.com",
  nodejs: "nodejs.org",
  react: "react.dev",
  python: "python.org",
};

/* Authored brand SVGs for individual technologies (rendered ahead of favicons). */
const TECH_LOGOS: Record<string, string> = {
  "amazon-connect": `<svg viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M484.311 292.676C484.311 311.287 484.311 320.593 480.689 327.701C477.503 333.954 472.419 339.038 466.167 342.224C459.058 345.846 449.753 345.846 431.142 345.846H351.449C343.321 345.846 339.257 345.846 335.432 346.764C332.042 347.578 328.8 348.921 325.827 350.743C322.473 352.798 319.6 355.672 313.852 361.419L306.033 369.239C300.286 374.986 297.412 377.86 295.357 381.213C293.535 384.186 292.193 387.428 291.379 390.818C290.46 394.643 290.46 398.707 290.46 406.835V431.138C290.46 449.749 290.46 459.055 286.839 466.163C283.653 472.416 278.569 477.5 272.316 480.686C265.208 484.308 255.902 484.308 237.291 484.308H205.171C186.56 484.308 177.254 484.308 170.146 480.686C163.893 477.5 158.809 472.416 155.623 466.163C152.001 459.055 152.001 449.749 152.001 431.138V399.015C152.001 380.405 152.001 371.099 155.623 363.991C158.809 357.738 163.893 352.654 170.146 349.468C177.254 345.846 186.559 345.846 205.17 345.846L229.078 345.846C237.206 345.846 241.27 345.846 245.094 344.928C248.485 344.114 251.726 342.771 254.699 340.949C258.053 338.894 260.927 336.02 266.674 330.273L274.896 322.05C280.643 316.303 283.517 313.429 285.572 310.076C287.393 307.103 288.736 303.861 289.55 300.471C290.468 296.646 290.468 292.582 290.468 284.455V205.169C290.468 186.558 290.468 177.253 294.09 170.144C297.276 163.892 302.36 158.808 308.613 155.622C315.721 152 325.027 152 343.637 152H431.142C449.753 152 459.058 152 466.167 155.622C472.419 158.808 477.503 163.892 480.689 170.144C484.311 177.253 484.311 186.558 484.311 205.169V292.676Z" fill="#E860FF"></path><path d="M871.988 389.6C871.988 408.211 871.988 417.516 868.366 424.625C865.18 430.878 860.096 435.961 853.844 439.147C846.735 442.769 837.43 442.769 818.819 442.769H642.411C634.283 442.769 630.219 442.769 626.394 443.687C623.003 444.501 619.762 445.844 616.789 447.666C613.435 449.721 610.561 452.595 604.814 458.342L596.796 466.36C591.049 472.108 588.175 474.981 586.12 478.335C584.298 481.308 582.956 484.55 582.142 487.94C581.224 491.765 581.224 495.829 581.224 503.956V624.985C581.224 643.596 581.224 652.901 577.602 660.01C574.416 666.262 569.332 671.346 563.079 674.532C555.971 678.154 546.665 678.154 528.054 678.154H407.03C398.902 678.154 394.838 678.154 391.013 679.072C387.623 679.886 384.381 681.229 381.408 683.051C378.054 685.106 375.181 687.98 369.433 693.727L361.415 701.745C355.668 707.492 352.795 710.366 350.74 713.72C348.918 716.693 347.575 719.934 346.761 723.325C345.843 727.149 345.843 731.213 345.843 739.341V818.831C345.843 837.442 345.843 846.747 342.221 853.856C339.035 860.108 333.951 865.192 327.699 868.378C320.59 872 311.285 872 292.674 872H205.169C186.558 872 177.253 872 170.144 868.378C163.892 865.192 158.808 860.108 155.622 853.856C152 846.747 152 837.442 152 818.831V731.323C152 712.712 152 703.407 155.622 696.298C158.808 690.045 163.892 684.962 170.144 681.776C177.253 678.154 186.558 678.154 205.169 678.154H284.656C292.784 678.154 296.848 678.154 300.672 677.236C304.063 676.422 307.305 675.079 310.278 673.257C313.631 671.202 316.505 668.328 322.252 662.581L330.27 654.563C336.017 648.815 338.891 645.942 340.946 642.588C342.768 639.615 344.111 636.374 344.925 632.983C345.843 629.158 345.843 625.094 345.843 616.967V495.938C345.843 477.327 345.843 468.022 349.465 460.914C352.651 454.661 357.734 449.577 363.987 446.391C371.096 442.769 380.401 442.769 399.012 442.769H520.036C528.164 442.769 532.228 442.769 536.053 441.851C539.444 441.037 542.685 439.694 545.658 437.872C549.012 435.817 551.886 432.943 557.633 427.196L565.651 419.178C571.398 413.431 574.272 410.557 576.327 407.204C578.149 404.23 579.491 400.989 580.305 397.598C581.224 393.774 581.224 389.71 581.224 381.582V205.169C581.224 186.558 581.224 177.253 584.845 170.144C588.031 163.892 593.115 158.808 599.368 155.622C606.476 152 615.782 152 634.393 152H818.819C837.43 152 846.735 152 853.844 155.622C860.096 158.808 865.18 163.892 868.366 170.144C871.988 177.253 871.988 186.558 871.988 205.169V389.6Z" fill="#FF6200"></path><path d="M872 680.368C872 698.979 872 708.284 868.378 715.393C865.192 721.646 860.109 726.729 853.856 729.915C846.747 733.537 837.442 733.537 818.831 733.537H739.139C731.011 733.537 726.947 733.537 723.122 734.455C719.731 735.269 716.49 736.612 713.517 738.434C710.163 740.489 707.289 743.363 701.542 749.11L693.73 756.923C687.983 762.67 685.109 765.544 683.054 768.897C681.232 771.87 679.889 775.112 679.075 778.503C678.157 782.327 678.157 786.391 678.157 794.519V818.829C678.157 837.44 678.157 846.746 674.535 853.854C671.349 860.107 666.266 865.191 660.013 868.377C652.904 871.999 643.599 871.999 624.988 871.999H592.867C574.256 871.999 564.951 871.999 557.842 868.377C551.59 865.191 546.506 860.107 543.32 853.854C539.698 846.746 539.698 837.44 539.698 818.829V786.706C539.698 768.095 539.698 758.79 543.32 751.681C546.506 745.429 551.59 740.345 557.842 737.159C564.951 733.537 574.256 733.537 592.867 733.537H616.765C624.893 733.537 628.957 733.537 632.781 732.619C636.172 731.805 639.413 730.462 642.387 728.64C645.74 726.585 648.614 723.711 654.361 717.964L662.585 709.74C668.332 703.993 671.205 701.119 673.26 697.766C675.082 694.793 676.425 691.551 677.239 688.161C678.157 684.336 678.157 680.272 678.157 672.144V592.86C678.157 574.249 678.157 564.944 681.779 557.835C684.965 551.582 690.049 546.499 696.302 543.313C703.41 539.691 712.715 539.691 731.326 539.691H818.831C837.442 539.691 846.747 539.691 853.856 543.313C860.109 546.499 865.192 551.582 868.378 557.835C872 564.944 872 574.249 872 592.86V680.368Z" fill="#00ABBA"></path></svg>`,
};

/* Small brand/category mark for an individual technology (authored SVG →
   favicon → category icon). */
export function TechMark({ id, size = 18 }: { id: string; size?: number }) {
  const t = getTechnology(id);
  const cat = t?.category;
  const hue = TECH_CAT_HUE[cat ?? ""] ?? "var(--accent)";
  const domain = TECH_DOMAIN[id];
  const [ok, setOk] = useState(Boolean(domain));
  const authored = TECH_LOGOS[id];
  if (authored) {
    return (
      <span
        className="tech-mark authored"
        style={{ width: size, height: size }}
        title={t?.label}
        dangerouslySetInnerHTML={{ __html: authored }}
      />
    );
  }
  if (domain && ok) {
    return (
      <span className="tech-mark" style={{ width: size, height: size }}>
        <img src={FAVICON(domain, 64)} alt="" onError={() => setOk(false)} loading="lazy" />
      </span>
    );
  }
  const CatIcon = techCatIcon(cat);
  return (
    <span
      className="tech-mark generic"
      style={{ width: size, height: size, color: hue, "--hue": hue } as CSSProperties}
      title={t?.label}
    >
      {/* eslint-disable-next-line react-hooks/static-components -- techCatIcon returns a stable icon component from a static map */}
      <CatIcon size={Math.round(size * 0.6)} />
    </span>
  );
}

export function TechnologyBlocks() {
  const groups = useMemo(() => {
    const usage = technologyUsage();
    return TECH_GROUPS.map((g) => ({
      ...g,
      items: usage
        .filter((t) => g.cats.includes(t.category))
        .sort((a, b) => b.last - a.last || b.weight - a.weight),
      hue: TECH_CAT_HUE[g.cats[0]] ?? "var(--accent)",
    })).filter((g) => g.items.length > 0);
  }, []);

  return (
    <div className="tech-groups">
      {groups.map((g) => {
        const GroupIcon = techGroupIcon(g.key);
        return (
          <section className="tech-group" key={g.key}>
            <header className="tech-group-head">
              <span
                className="tech-group-ico"
                style={{
                  color: g.hue,
                  background: `color-mix(in oklab, ${g.hue} 12%, var(--card))`,
                  borderColor: `color-mix(in oklab, ${g.hue} 26%, var(--rule))`,
                }}
              >
                <GroupIcon size={15} />
              </span>
              <span className="tech-group-name mono">{g.name}</span>
              <span className="tech-group-count mono">{g.items.length}</span>
            </header>
            <ul className="tech-chiprow">
              {g.items.map((t) => (
                <li key={t.id}>
                  <Link
                    to={`/by/technology?tech=${t.id}`}
                    className="tech-chip card-link"
                    title={t.label}
                  >
                    <TechMark id={t.id} size={18} />
                    <span className="tech-chip-label">{t.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
